/**
 * ValidatorService is a service class responsible for managing validation messages and callbacks.
 * It interacts with various services to handle validation logic and messages.
 */
class ValidatorService {
    /**
     * Constructor for ValidatorService.
     * @param {Object} $injector - The AngularJS injector service.
     */
    constructor($injector) {
        if (!isCMS) {
            return false;
        }

        this._logIdentifier = 'ValidatorService';
        this.$logger = $injector.get('LogBridgeService');
        this.$logger.info(this, 'initialize');

        this._TaskDataService = null;
        this._SheetLeaveService = null;
        this._translate = null;
        this._config = null;
        this._routeParams = null;
        this._rootScope = null;

        this._callBackObjects = [];
        this._validatorMessages = [];
        this._sheetLeaveCallBackName = 'ValidatorService_sheetOnBlurErrors';
        this._fetchContext = null;
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} - The TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the SheetLeaveService instance.
     * @returns {Object} - The SheetLeaveService instance.
     */
    getSheetLeaveService() {
        if (!this._SheetLeaveService) {
            this._SheetLeaveService = this.$injector.get('SheetLeaveService');
        }
        return this._SheetLeaveService;
    }

    /**
     * Get the $translate service instance.
     * @returns {Object} - The $translate service instance.
     */
    getTranslate() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Get the $config service instance.
     * @returns {Object} - The $config service instance.
     */
    getConfig() {
        if (!this._config) {
            this._config = this.$injector.get('$config');
        }
        return this._config;
    }

    /**
     * Get the $routeParams service instance.
     * @returns {Object} - The $routeParams service instance.
     */
    getRouteParams() {
        if (!this._routeParams) {
            this._routeParams = this.$injector.get('$routeParams');
        }
        return this._routeParams;
    }

    /**
     * Get the $rootScope service instance.
     * @returns {Object} - The $rootScope service instance.
     */
    getRootScope() {
        if (!this._rootScope) {
            this._rootScope = this.$injector.get('$rootScope');
        }
        return this._rootScope;
    }

    /**
     * Get the fetch context.
     * @returns {Object} - The fetch context.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Bind a callback function to an error event using a UUID.
     * @param {string} uuid - The UUID identifier.
     * @param {Function} fn - The callback function.
     */
    bindOnErrorCallBack(uuid, fn) {
        let callBack = _.find(this._callBackObjects, {'uuid': uuid});
        if (callBack) {
            let old_fn = angular.copy(callBack.fn);
            callBack.fn = function () {
                old_fn();
                fn();
            }
        } else {
            this._callBackObjects.push({
                'uuid': uuid,
                'fn':   fn
            });
        }
    }

    /**
     * Call the callback function associated with the given UUID.
     * @param {string} uuid - The UUID identifier.
     * @param {Object} args - The arguments to pass to the callback function.
     */
    callOnErrorCallBack(uuid, args) {
        let callBack = _.find(this._callBackObjects, {'uuid': uuid});
        if (callBack && callBack.fn && angular.isFunction(callBack.fn)) {
            callBack.fn(args);
        }
    }

    /**
     * Unbind the callback function associated with the given UUID.
     * @param {string} uuid - The UUID identifier.
     */
    unbindOnErrorCallBack(uuid) {
        let callBackIndex = _.findIndex(this._callBackObjects, {'uuid': uuid});
        if (callBackIndex > -1) {
            this._callBackObjects.splice(callBackIndex, 1);
        }
    }

    /**
     * Add a validation message.
     * @param {Object} message - The validation message to add.
     */
    addValidatorMessages(message) {
        this._validatorMessages.push(message);
    }

    /**
     * Remove a validation message by model UUID and validator.
     * @param {string} modelUUID - The model UUID.
     * @param {string} validator - The validator name.
     */
    removeValidatorMessages(modelUUID, validator) {
        let validatorMessagesIndex = _.findIndex(this._validatorMessages, {
            'modelUUID': modelUUID,
            'validator': validator
        });
        if (validatorMessagesIndex > -1) {
            this._validatorMessages.splice(validatorMessagesIndex, 1);
        }
    }

    /**
     * Remove all validation messages associated with a model UUID.
     * @param {string} modelUUID - The model UUID.
     */
    removeAllValidatorMessagesByUUID(modelUUID) {
        let validatorMessages = _.filter(this._validatorMessages, {
            'modelUUID': modelUUID
        });

        for (let key in validatorMessages) {
            let message = validatorMessages[key];

            let validatorMessagesIndex = _.findIndex(this._validatorMessages, {
                'modelUUID': message.modelUUID
            });

            if (validatorMessagesIndex > -1) {
                this._validatorMessages.splice(validatorMessagesIndex, 1);
                this.unbindOnErrorCallBack(message.modelUUID);
            }
        }
    }

    /**
     * Get validation messages by model UUID.
     * @param {string} modelUUID - The model UUID.
     * @returns {Array} - The validation messages.
     */
    getValidatorMessagesByModelUUID(modelUUID) {
        return _.filter(this._validatorMessages, {'modelUUID': modelUUID});
    }

    /**
     * Get validation messages for sheet on blur errors.
     * @param {Object} sheet - The sheet object.
     * @returns {Array} - The validation messages.
     */
    getSheetOnBlurErrors(sheet) {
        let result = [];

        if (!sheet || this.getConfig().isPreviewMode()) return result;

        let messages = _.filter(this._validatorMessages, {'onSheetBlur': true});

        for (let key in messages) {
            let message = messages[key];

            let parents = this.getFetchContext().getParentsEntryByUUID(message.modelUUID);
            let parent_sheet = _.find(parents, function (item) {
                return item.getType() === 'sheet';
            });

            if (parent_sheet && parent_sheet.getUUID() === sheet.getUUID()) {
                result.push(message);
            }
        }

        return result;
    }

    /**
     * Check if there are any validation messages.
     * @returns {boolean} - True if there are validation messages, false otherwise.
     */
    existsValidatorMessages() {
        return this._validatorMessages.length > 0;
    }

    /**
     * Check if a specific validation message exists.
     * @param {string} modelUUID - The model UUID.
     * @param {string} validator - The validator name.
     * @returns {Array} - The validation messages.
     */
    existsValidatorMessage(modelUUID, validator) {
        return _.filter(this._validatorMessages, {
            'modelUUID': modelUUID,
            'validator': validator
        });
    }

    /**
     * Get all validation messages.
     * @returns {Array} - The validation messages.
     */
    getValidatorMessages() {
        return angular.copy(this._validatorMessages);
    }

    /**
     * Destroy the validation service instance.
     */
    destroyValidationServiceInstance() {
        this._validatorMessages = [];
        this._callBackObjects = [];
    }

    /**
     * Get the current sheet.
     * @returns {Object|undefined} - The current sheet or undefined if not found.
     */
    getCurrentSheet() {
        let sheetUUID = this.getRouteParams().sheetUUID;
        if (!angular.isUUID(sheetUUID)) return undefined;
        return this.getFetchContext().exists(sheetUUID) && this.getFetchContext().read(sheetUUID);
    }

    /**
     * Get the current task.
     * @returns {Object|undefined} - The current task or undefined if not found.
     */
    getCurrentTask() {
        let taskUUID = this.getRouteParams().taskUUID;
        if (!angular.isUUID(taskUUID) || !this.getConfig().isOnEditSheet()) return undefined;
        return this.getFetchContext().exists(taskUUID) && this.getFetchContext().read(taskUUID);
    }

    /**
     * Callback function for sheet on blur errors.
     * @param {Object} sheet - The sheet object.
     * @returns {Object} - The result object containing error status and messages.
     */
    _sheetOnBlurErrorsCallBackFn(sheet) {
        let result = {
            error:    false,
            messages: []
        };
        let messages = this.getSheetOnBlurErrors(sheet);
        if (messages && messages.length > 0) {
            result.error = true;
            for (let key in messages) {
                let message = messages[key];

                if (angular.isUUID(message.modelUUID) && this.getFetchContext().exists(message.modelUUID)) {
                    let parents = this.getFetchContext().getParentsEntryByUUID(message.modelUUID);
                    let question_part = _.find(parents, function (item) {
                        return item.getType() === 'questionPart';
                    });
                }

                result.messages.push({
                    'title':       this.getTranslate().instant('CMS.FACTORIES.MODELS.VALIDATORS.BANNER.HEADLINES.' + message.validator),
                    'message':     message.message,
                    'status':      message.status,
                    'group':       question_part ? question_part.getUUID() : false,
                    'message_key': message.validator
                });
            }
            return result;
        }
        return result;
    }

    /**
     * Initialize the sheet on blur errors.
     */
    _initSheetOnBlurErrors() {
        let sheet = this.getCurrentSheet();
        if (sheet) {
            this.getSheetLeaveService().bindOnLeaveCallBack(this._sheetLeaveCallBackName, this._sheetOnBlurErrorsCallBackFn, sheet);
        } else {
            this.getSheetLeaveService().unbindOnErrorCallBack(this._sheetLeaveCallBackName)
        }
    }

    /**
     * Initialize the ValidatorService.
     */
    init() {
        this._initSheetOnBlurErrors();

        this.getRootScope().$on('$routeChangeSuccess', (next, current) => {
            if (!this.getCurrentTask()) {
                this._validatorMessages = [];
                this.callOnErrorCallBack('validation_banner_directive');
            }
            this._initSheetOnBlurErrors();
        });
    }
}

export default ValidatorService;